import { useMemo, useState } from 'react';
import { BaseModal } from '@/components/shared';
import { Alert } from '@/components/Alert';
import { useClientsQuery } from '@/features/Clients/Data';
import type { ProcessoMovimentacao, MetodoEntrega } from '../../../models';
import { useEstadiaClienteMutations, useRegrasClienteQuery } from '../../../hooks/data';
import {
  PROCESSO_MOVIMENTACAO_LABELS,
  PROCESSO_MOVIMENTACAO_OPCOES,
  METODO_ENTREGA_LABELS,
  METODOS_COM_DATA_UNICA,
} from '../equipamentosListPage.definitions';
import LinhaEnviarEquipamento, { type LinhaEquipamentoState } from './LinhaEnviarEquipamento';

/** Linha "tocada" (usuário começou a preencher) mas ainda sem o ativo do Inventário. Usado
 * só pra dar feedback, nunca pra travar campos que o usuário nem começou a preencher. */
function linhaIncompleta(linha: LinhaEquipamentoState): boolean {
  const tocada = Boolean(linha.patrimonio.trim() || linha.nSerie.trim());
  return tocada && !linha.ativo;
}

interface EnviarParaClienteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

function linhaVazia(): LinhaEquipamentoState {
  return { id: crypto.randomUUID(), patrimonio: '', nSerie: '', modelo: '', colaboradorCotado: null, ativo: null };
}

/**
 * Ação "Enviar máquina(s) para Cliente" — Fase 1 do fluxo de máquina homologada por
 * Cliente, ANTES de existir qualquer Solicitação. Busca por patrimônio/nº série + GLPI
 * (mesmo padrão de VincularEquipamentoSection) em vez de escolher entre equipamentos já
 * cadastrados em si_equipamentos — o inventário do GLPI só é lido (nunca sincronizado por
 * escrita), então nem todo equipamento em estoque na Raro está cadastrado no Radar ainda;
 * restringir a isso deixaria de fora a maior parte do estoque real. Cria 1 EstadiaCliente
 * por linha preenchida (achando ou criando o Equipamento correspondente, ver
 * useEstadiaClienteMutations.criarEstadias) — não é atômico, aceitável dado o volume baixo
 * esperado.
 */
export default function EnviarParaClienteDrawer({ isOpen, onClose }: EnviarParaClienteDrawerProps) {
  const { criarEstadias } = useEstadiaClienteMutations();
  const { data: clientes = [] } = useClientsQuery();
  const { data: regrasCliente = [] } = useRegrasClienteQuery();

  // Esse fluxo é só pra Clientes que exigem máquina homologada — não faz sentido buscar
  // entre todos os Clientes do sistema, a lista real é sempre pequena (checkbox/radio, não
  // combobox de busca, mesmo padrão do resto do sistema).
  const clientesHomologados = useMemo(
    () =>
      clientes.filter(
        (cliente) => regrasCliente.find((regra) => regra.clienteId === cliente.id)?.usaEquipamentoHomologado === true
      ),
    [clientes, regrasCliente]
  );

  const [clienteId, setClienteId] = useState<string | null>(null);
  const [motivo, setMotivo] = useState<ProcessoMovimentacao>('homologacao');
  const [dataEnvio, setDataEnvio] = useState('');
  const [formaEnvio, setFormaEnvio] = useState<MetodoEntrega | ''>('');
  const [codigoRastreio, setCodigoRastreio] = useState('');
  const [linhas, setLinhas] = useState<LinhaEquipamentoState[]>([linhaVazia()]);

  const usaDataUnica = formaEnvio ? METODOS_COM_DATA_UNICA.includes(formaEnvio) : false;

  const handleClose = () => {
    setClienteId(null);
    setMotivo('homologacao');
    setDataEnvio('');
    setFormaEnvio('');
    setCodigoRastreio('');
    setLinhas([linhaVazia()]);
    onClose();
  };

  const handleChangeLinha = (linhaAtualizada: LinhaEquipamentoState) => {
    setLinhas((atual) => atual.map((linha) => (linha.id === linhaAtualizada.id ? linhaAtualizada : linha)));
  };

  const handleRemoverLinha = (id: string) => {
    setLinhas((atual) => (atual.length > 1 ? atual.filter((linha) => linha.id !== id) : atual));
  };

  // Só vai o que existe no Inventário (busca + "cadastrar agora" na própria linha).
  const linhasValidas = linhas.filter((linha) => linha.ativo);
  const podeEnviar = Boolean(clienteId) && linhasValidas.length > 0;

  const camposFaltando: string[] = [];
  if (!clienteId) camposFaltando.push('Cliente');
  if (linhasValidas.length === 0) camposFaltando.push('Pelo menos um equipamento do Inventário (busque pelo patrimônio)');

  const handleEnviar = () => {
    if (!clienteId || linhasValidas.length === 0) return;

    criarEstadias.mutate(
      {
        clienteId,
        motivo,
        dataEnvio: dataEnvio || null,
        formaEnvio: formaEnvio || null,
        codigoRastreio: codigoRastreio.trim() || null,
        equipamentos: linhasValidas.map((linha) => ({
          equipamentoId: linha.ativo!.id,
          patrimonio: linha.ativo!.patrimonio ?? linha.patrimonio.trim(),
          nSerie: linha.ativo!.nSerie ?? (linha.nSerie.trim() || null),
          modelo: linha.ativo!.modelo ?? (linha.modelo.trim() || null),
          colaboradorCotadoId: linha.colaboradorCotado?.id ?? null,
        })),
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title="Enviar máquina(s) para Cliente" maxWidth="max-w-2xl">
      <div className="space-y-4 py-4">
        <div>
          <p className="text-sm font-medium text-gray-700">Cliente *</p>
          {clientesHomologados.length === 0 ? (
            <p className="mt-1 text-sm text-gray-500">
              Nenhum Cliente configurado com "Equipamento Homologado" ainda (ver Configurações).
            </p>
          ) : (
            <div className="mt-1 flex flex-wrap gap-3">
              {clientesHomologados.map((cliente) => (
                <label key={cliente.id} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="enviar-para-cliente"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={clienteId === cliente.id}
                    onChange={() => setClienteId(cliente.id)}
                  />
                  {cliente.name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Motivo do envio *</label>
          <select
            className="input-primary mt-1 w-full"
            value={motivo}
            onChange={(event) => setMotivo(event.target.value as ProcessoMovimentacao)}
          >
            {PROCESSO_MOVIMENTACAO_OPCOES.map((valor) => (
              <option key={valor} value={valor}>
                {PROCESSO_MOVIMENTACAO_LABELS[valor]}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Forma de envio</label>
              <select
                className="input-primary mt-1 w-full"
                value={formaEnvio}
                onChange={(event) => setFormaEnvio(event.target.value as MetodoEntrega | '')}
              >
                <option value="">Selecione...</option>
                {(Object.entries(METODO_ENTREGA_LABELS) as [MetodoEntrega, string][]).map(([valor, label]) => (
                  <option key={valor} value={valor}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {formaEnvio === 'correios' && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">Código de rastreio</label>
                <input
                  type="text"
                  className="input-primary mt-1 w-full"
                  value={codigoRastreio}
                  onChange={(event) => setCodigoRastreio(event.target.value)}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {usaDataUnica ? 'Data de entrega' : 'Data de envio'}
            </label>
            <input
              type="date"
              className="input-primary mt-1 w-full max-w-xs"
              value={dataEnvio}
              onChange={(event) => setDataEnvio(event.target.value)}
            />
            {!usaDataUnica && formaEnvio && (
              <p className="mt-1 text-xs text-gray-500">
                A chegada no Cliente é confirmada depois, no card da estadia.
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {linhas.map((linha) => (
            <div key={linha.id}>
              <LinhaEnviarEquipamento
                linha={linha}
                onChange={handleChangeLinha}
                onRemover={linhas.length > 1 ? () => handleRemoverLinha(linha.id) : undefined}
              />
              {linhaIncompleta(linha) && (
                <p className="mt-1 text-xs text-amber-600">
                  Busque o equipamento no Inventário (ou cadastre) pra ele entrar no envio.
                </p>
              )}
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => setLinhas((atual) => [...atual, linhaVazia()])}
          >
            + Adicionar equipamento
          </button>
        </div>

        {camposFaltando.length > 0 && (
          <Alert variant="warning" title="Campos obrigatórios pendentes" description={`Preencha: ${camposFaltando.join(', ')}`} />
        )}

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={handleEnviar} disabled={!podeEnviar || criarEstadias.isPending}>
            {criarEstadias.isPending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
